import { Injectable, inject, signal } from "@angular/core";
import { ProfileService } from "../../../../../services/profile.service";
import { ApiService } from "../../../../../services/api.service";
import { AgentService } from "../../../../agent/services/agent.service";
import { AlertService } from "../../../../../shared/alert/alert.service";
import { AlertEnum } from "../../../../../shared/alert/alert.enum";
import { ISensitivityLabel } from "../../../../../interfaces/i-sensitivity-label";
import { IInstructions } from "../../../interfaces/i-instructions";
import { InstructionType } from "../../../../../enums/automation.enum";
import { environment } from "../../../../../../environments/environment";
import { toISensitivityLabel } from "../../../../../utilities/sensitivity-label.utils";
import { SensitivityLabelService } from "../../../../../services/sensitivity-label.service";
import { IUser } from "../../../../../interfaces/i-user-profile";
import { IAgentsResponse } from "../../../../../interfaces/i-agents";

@Injectable({
  providedIn: "root",
})
export class InstructionBuilderService {
  // NOTE: `inject()` fields must be declared before the signal fields below, because
  // `instruction`'s initializer calls `createInstructionDefaultValue()`, which reads
  // `#sensitivityLabelService`. Class field initializers run in declaration order, so the
  // injected fields must exist first or the private-member access throws at construction.
  #profileService = inject(ProfileService);
  #apiService = inject(ApiService);
  #agentService = inject(AgentService);
  #alertService = inject(AlertService);
  #sensitivityLabelService = inject(SensitivityLabelService);

  instruction = signal<IInstructions>(this.createInstructionDefaultValue());
  sensitivityLabels = signal<ISensitivityLabel[]>([]);
  agentId = signal<string>("");

  constructor() {
    const owner: IUser = {
      name: this.#profileService.userProfile()!.profile.name,
      userId: this.#profileService.userProfile()!.profile.email,
    };

    // Check if the owner already exists in the list of owners
    const ownerExists = this.instruction().details.owners.some(
      (existingOwner) => existingOwner.userId === owner.userId
    );
    // Add the owner to the instruction after the user has logged in.
    // We need to wait for the initialization to be complete to get the user profile.
    if (!ownerExists) {
      this.instruction().details.owners.push(owner);
    }
  }

  /**
   * Creates a default instruction object with initial values
   * @returns IInstructions A default instruction object
   */
  createInstructionDefaultValue(): IInstructions {
    // Safely access environment variables
    const modelName = environment.defaultModelConfig?.model;
    const accountName = environment.defaultModelConfig?.accountName;

    const defaultInstruction: IInstructions = {
      id: "",
      details: {
        name: "",
        description: "",
        type: InstructionType.AiPrompt,
        prependedPrompt: "",
        prompt: "",
        keywords: [],
        model: { model: modelName, accountName: accountName },
        temperature: 0.5,
        topP: 0.5,
        agent: "",
        fileUploadLabel: "",
        collectInformationLabel: "",
        sensitivityLabels: this.#sensitivityLabelService.labels().slice(0, 2).map(l => toISensitivityLabel(l)),
        isEnabled: true,
        isUniversal: false,
        owners: [],
        users: [],
        groups: [],
      },
    };

    return defaultInstruction;
  }

  /**
   * Resets the instruction to default values
   */
  resetInstruction(): void {
    const defaultInstruction = this.createInstructionDefaultValue();

    if (this.#profileService.userProfile()) {
      const owner = {
        name: this.#profileService.userProfile()!.profile.name,
        userId: this.#profileService.userProfile()!.profile.email,
      };
      defaultInstruction.details.owners.push(owner);
      // Add the owner to the instruction
      this.instruction.update(() => defaultInstruction);
    }
  }

  /**
   * Returns the agent ID for the Read CGI Federal Agent.
   * This is for the Instructions to use when a user selects the Upload File type.
   * @return {Promise<string>} A promise that resolves to the agent ID for the Read CGI Federal Agent.
   */
  async getReadCgiFederalFileAgentId(): Promise<string> {
    if (this.#agentService.agents().agents.length === 0) {
      try {
        const response = await this.#apiService.getAgents().toPromise();
        if (!response) {
          throw new Error("No response received from getAgents");
        }
        const data: IAgentsResponse = response;
        const updatedData = this.#agentService.convertAgentsToArray(data);
        this.#agentService.agents.set(updatedData);
      } catch (error) {
        this.#alertService.showAlert({
          message: `Failed to get all agents.`,
          type: AlertEnum.error,
        });
        throw new Error("Failed to get agents");
      }
    }

    const readCgiFederalAgent = this.#agentService
      .agents()
      .agents.find((agent) => agent.details.name === "Read CGI Federal File");

    if (!readCgiFederalAgent) {
      throw new Error("Read CGI Federal File agent not found");
    }

    return readCgiFederalAgent.id;
  }
}
